import { useMemo } from 'react';
import { useEmployees } from '@/hooks/useApi';

export interface LocationPosition {
  id: string;
  name: string;
  employee_count: number;
}

interface EmployeeShape {
  id: string;
  is_active?: boolean;
  position?: { id: string; name: string } | null;
  position_id?: string | null;
  department?: { id: string } | null;
  department_id?: string | null;
}

/**
 * Возвращает уникальные должности активных сотрудников локации, отсортированные
 * по количеству сотрудников (по убыванию). Для UI multi-select per-position
 * запуска оптимизации.
 *
 * Если задан непустой departmentIds — фильтрует только сотрудников из этих
 * подразделений.
 *
 * Использует существующий /employees endpoint (limit=500) и derived-логику —
 * отдельный backend endpoint не нужен.
 */
export function useLocationPositions(
  locationId: string | null,
  departmentIds?: string[] | null,
) {
  const query = useEmployees(
    locationId ? { location_id: locationId, limit: 500 } : {},
    { enabled: !!locationId },
  );

  const deptKey = (departmentIds ?? []).join(',');

  const positions = useMemo<LocationPosition[]>(() => {
    const data = (query.data as { data?: EmployeeShape[] })?.data;
    if (!data) return [];
    const deptSet = deptKey ? new Set(deptKey.split(',')) : null;
    const counter = new Map<string, { name: string; n: number }>();
    for (const e of data) {
      if (e.is_active === false) continue;
      if (deptSet) {
        const empDeptId = e.department?.id ?? e.department_id ?? null;
        if (!empDeptId || !deptSet.has(empDeptId)) continue;
      }
      const id = e.position?.id ?? e.position_id ?? null;
      const name = e.position?.name ?? null;
      if (!id || !name) continue;
      const cur = counter.get(id);
      if (cur) cur.n += 1;
      else counter.set(id, { name, n: 1 });
    }
    return Array.from(counter.entries())
      .map(([id, v]) => ({ id, name: v.name, employee_count: v.n }))
      .sort((a, b) => b.employee_count - a.employee_count);
  }, [query.data, deptKey]);

  return {
    positions,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
